/**
 * Lớp phủ DOM trên canvas: chọn buff giữa tầng (rogue-lite), màn thắng/thua + chơi lại.
 * DOM thay vì Pixi để chữ sắc nét, chạm/nút bấm chuẩn và test e2e chọn được bằng data-testid.
 */
import type { RunState } from '@ml/core';
import type { Skin } from '../art/skin.ts';
import { buffText, INERT_BUFF_TYPES } from './overlay-text.ts';

function buffName(id: string): string {
  return id.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}

export class Overlay {
  readonly el: HTMLDivElement;

  constructor(
    private readonly skin: Skin | null,
    private readonly atlasUrl: string,
  ) {
    this.el = document.createElement('div');
    this.el.id = 'overlay';
    this.el.hidden = true;
    document.body.appendChild(this.el);
  }

  get visible(): boolean {
    return !this.el.hidden;
  }

  hide(): void {
    this.el.hidden = true;
    this.el.replaceChildren();
    delete this.el.dataset.kind;
  }

  private icon(key: string, scale: number): HTMLElement {
    const span = document.createElement('span');
    span.className = 'icon';
    const r = this.skin?.rect(key);
    if (r && this.skin) {
      const { w, h } = this.skin.atlas.size;
      Object.assign(span.style, {
        width: `${r.w * scale}px`,
        height: `${r.h * scale}px`,
        backgroundImage: `url(${this.atlasUrl})`,
        backgroundSize: `${w * scale}px ${h * scale}px`,
        backgroundPosition: `-${r.x * scale}px -${r.y * scale}px`,
      });
    } else span.classList.add('icon-missing');
    return span;
  }

  showBuffs(run: RunState, onPick: (index: number) => void): void {
    this.el.replaceChildren();
    this.el.dataset.kind = 'buff';
    const box = document.createElement('div');
    box.className = 'panel';
    const h = document.createElement('h2');
    h.textContent = `Thắng tầng ${run.floor + 1}! Chọn 1 phúc lành`;
    const hp = document.createElement('p');
    const hs = run.battle.heroState;
    hp.textContent = `HP ${hs.hp}/${hs.maxHp} (đã hồi giữa tầng)`;
    box.append(h, hp);
    run.offer.forEach((b, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `buff rarity-${b.rarity}`;
      btn.dataset.testid = `buff-${i}`;
      btn.dataset.buff = b.id;
      const name = document.createElement('strong');
      name.textContent = buffName(b.id);
      const desc = document.createElement('span');
      desc.className = 'desc';
      desc.textContent = buffText(b);
      const rar = document.createElement('em');
      rar.textContent = b.rarity;
      const text = document.createElement('span');
      text.className = 'text';
      text.append(name, desc, rar);
      btn.append(this.icon(`buff.${b.id}`, 3), text);
      if (INERT_BUFF_TYPES.has(b.type)) btn.classList.add('inert');
      btn.addEventListener('click', () => onPick(i), { once: true });
      box.append(btn);
    });
    this.el.append(box);
    this.el.hidden = false;
  }

  showEnd(run: RunState, onRestart: () => void): void {
    this.el.replaceChildren();
    const won = run.phase === 'won';
    this.el.dataset.kind = won ? 'won' : 'lost';
    const box = document.createElement('div');
    box.className = 'panel';
    const h = document.createElement('h2');
    h.textContent = won ? 'Chiến thắng! Hầm mộ đã sạch' : `Thất bại ở tầng ${run.floor + 1}`;
    const p = document.createElement('p');
    p.textContent = `Vòng: ${run.stats.rounds} · Đòn mạnh nhất: ${run.stats.maxDamage} · Booster: ${run.stats.boostersCreated}`;
    const buffs = document.createElement('p');
    buffs.textContent = run.buffs.length
      ? `Phúc lành: ${run.buffs.map((b) => buffName(b.id)).join(', ')}`
      : '';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'restart';
    btn.dataset.testid = 'restart';
    btn.textContent = 'Chơi lại';
    btn.addEventListener('click', () => onRestart(), { once: true });
    box.append(h, p, buffs, btn);
    this.el.append(box);
    this.el.hidden = false;
  }
}
